/* hs-eslint ignored failing-rules */
/* eslint-disable hubspot-dev/no-confusing-browser-globals */

'use es6';

import { arrayContainsStr, getAndRemoveCallValueFromQueue } from 'cookie-banner/utils/hspbUtils';
import { COOKIES_BY_CATEGORY, COOKIES_WITHOUT_BANNER, NO_COOKIES, OPT_IN } from 'cookie-banner/constants/privacyPolicy';
import CookiesByCategoryHandler from 'cookie-banner/handlers/cookiesByCategoryHandler';
import Consent from 'cookie-banner/models/Consent';
import CritsitCookieHandler from 'cookie-banner/handlers/critsitCookieHandler';
import { FACEBOOK_PIXEL_COOKIE } from 'cookie-banner/constants/trackingCookies';
import { getBannerPolicyFromPrivacySetting } from 'cookie-banner/constants/privacyPolicy';
import { getCountry } from 'cookie-banner/utils/geolocationApi';
import { getSettingsMatchLevel, CONFIG_TYPE_TO_MATCH_LEVEL } from 'cookie-banner/utils/policyResolution';
import { getTestingCountryCode, logDebug, logError } from 'cookie-banner/utils/hspbUtils';
import { HSQ_QUEUE_NAME, PRIVACY_TESTING_FLAG } from 'cookie-banner/constants/hspb';
import OptInHandler from 'cookie-banner/handlers/optInHandler';
import PrivacySettings from 'cookie-banner/models/PrivacySettings';
import { sendClickActivity, sendViewActivity } from 'cookie-banner/utils/reportingApi';
import TRACKING_COOKIES from 'cookie-banner/constants/trackingCookies';
import { sendConsentUpdateViaGCMV2 } from './utils/GoogleConsentModeV2API';
import { getCurrentGeoLocation } from './utils/geolocationApi';
export default class Banner {
  constructor(context, cookie) {
    this.context = context;
    this.cookie = cookie;
    this.cookiesByCategoryHandler = null;
    this.optInHandler = null;
    this.apiBaseUrl = null;
    this.privacyConsent = null;
    this.privacySettings = null;
    this.privacyConsentListeners = [];
    this.privacySettingsListeners = [];
    this.useGCMV2 = false;
    this.gcmGeoLocationPromise = null;
    this.visitorCountry = null;
    this.enabledFeatureGates = [];
    this.isTestingEnabled = context.getLocation().hash.indexOf(PRIVACY_TESTING_FLAG) > -1 || context.getLocation().search.indexOf(PRIVACY_TESTING_FLAG) > -1;
    this.portalId = null;
    this.businessUnitId = null;
  }
  initialize(hsp, hasGeoTargetingCall) {
    const testingCountryCode = getTestingCountryCode(this.context.getLocation().search);
    if (testingCountryCode) {
      this.visitorCountry = testingCountryCode;
    }
    const privacyPolicies = getAndRemoveCallValueFromQueue(hsp, 'setCookiePolicy');
    const shouldRunGeoTargeting = hasGeoTargetingCall && !testingCountryCode;
    if (shouldRunGeoTargeting) {
      getCountry(this.apiBaseUrl, response => {
        this.visitorCountry = response;
        this.setCookiePolicy(privacyPolicies);
      });
    } else {
      this.setCookiePolicy(privacyPolicies);
    }
    const runCritsitCookieScrubbing = getAndRemoveCallValueFromQueue(hsp, 'runCookieScrubbing');
    if (runCritsitCookieScrubbing) {
      this.runCritsitCookieScrubbing();
    }
  }
  setCookiePolicy(privacyPolicies) {
    if (!privacyPolicies || this.privacySettings) {
      return;
    }
    if (privacyPolicies.length === 0) {
      this.setPrivacyConsent(true);
      return;
    }
    const policyTesting = this.isTestingEnabled;
    let defaultSettings;
    let highestMatchLevel = CONFIG_TYPE_TO_MATCH_LEVEL.noMatch;
    let mostSpecificPolicy;
    for (let i = 0; i < privacyPolicies.length; i++) {
      const settings = privacyPolicies[i];
      const isPolicyEnabled = settings.enabled || policyTesting;
      if (settings.privacyDefault && isPolicyEnabled) {
        defaultSettings = new PrivacySettings(settings, policyTesting);
        continue;
      }
      const candidatePolicy = new PrivacySettings(settings, policyTesting);
      const candidateMatchLevel = getSettingsMatchLevel(settings, policyTesting, this.context, this.visitorCountry);
      if (candidateMatchLevel > highestMatchLevel) {
        highestMatchLevel = candidateMatchLevel;
        mostSpecificPolicy = candidatePolicy;
      }
    }
    const finalSettings = mostSpecificPolicy || defaultSettings;
    this.setPrivacySettings(finalSettings);
    if (!finalSettings) {
      this.setPrivacyConsent(true);
      return;
    }
    logDebug(`banner matched cookie policy ${finalSettings.label}`);
    switch (finalSettings.mode) {
      case COOKIES_WITHOUT_BANNER:
        this.setPrivacyConsent(true);
        return;
      case NO_COOKIES:
        this.setPrivacyConsent(false);
        return;
      case OPT_IN:
        this.optInHandler = new OptInHandler(this, this.privacySettings);
        this.optInHandler.handle();
        return;
      case COOKIES_BY_CATEGORY:
        this.cookiesByCategoryHandler = new CookiesByCategoryHandler(this, this.privacySettings);
        this.cookiesByCategoryHandler.handle();
        return;
      default:
        logError(new Error(`Unknown privacy mode: ${finalSettings.mode}`));
        this.setPrivacyConsent(true);
        return;
    }
  }
  runCritsitCookieScrubbing() {
    if (!this.privacySettings || this.privacySettings.mode === 'DISABLED') {
      return;
    }
    try {
      CritsitCookieHandler.handle(this.cookie, this.privacySettings);
    } catch (e) {
      logError(e);
    }
  }
  addPrivacyConsentListener(listener) {
    if (this.privacyConsent && !this.isTestingEnabled) {
      this.safeCallListener(listener, this.privacyConsent);
    }
    this.privacyConsentListeners.push(listener);
  }
  addPrivacySettingsListener(listener) {
    if (this.privacySettings) {
      this.safeCallListener(listener, this.privacySettings);
    }
    this.privacySettingsListeners.push(listener);
  }
  setPrivacyConsent(consent, {
    reporting
  } = {
    reporting: false
  }) {
    const previousCategories = this.privacyConsent && this.privacyConsent.categories;
    this.privacyConsent = consent instanceof Consent ? consent.getSafeCopy() : new Consent(consent);
    if (previousCategories) {
      this.privacyConsent.previousCategories = previousCategories;
    }
    if (!this.isTestingEnabled) {
      this.privacyConsentListeners.forEach(listener => this.safeCallListener(listener, this.privacyConsent.getSafeCopy()));
      if (this.useGCMV2) {
        this.sendConsentToGCMV2(this.privacyConsent.getSafeCopy());
      }
      this.cleanUpCookies();
    }
    if (reporting) {
      this.sendReportingClickActivity();
    }
  }
  setPrivacySettings(privacySettings) {
    this.privacySettings = privacySettings;
    this.privacySettingsListeners.forEach(listener => this.safeCallListener(listener, this.privacySettings));
  }
  getHsq() {
    const hsq = this.context.getWindow()[HSQ_QUEUE_NAME] = this.context.getWindow()[HSQ_QUEUE_NAME] || [];
    return hsq;
  }
  trackAction(action) {
    this.getHsq().push([action]);
  }
  revokeCookieConsent() {
    for (let i = 0; i < TRACKING_COOKIES.length; i++) {
      this.cookie.remove(TRACKING_COOKIES[i]);
    }
    this.setPrivacyConsent(false);
    this.trackAction('trackRevokeCookieConsent');
  }
  cleanUpCookies() {
    const hasChanged = this.privacyConsent.previousCategories.advertisement !== this.privacyConsent.categories.advertisement;
    if (hasChanged && !this.privacyConsent.categories.advertisement) {
      try {
        this.cookie.removeFBPCookie(FACEBOOK_PIXEL_COOKIE);
      } catch (e) {
        logError(e);
      }
    }
  }
  safeCallListener(listener, arg) {
    try {
      listener(arg);
    } catch (e) {
      logError(e);
    }
  }
  addCookieDomain(domain) {
    this.cookie.addDomain(domain);
  }
  setCookiesToSubdomain(cookiesToSubdomain) {
    this.cookie.setCookiesToSubdomain(cookiesToSubdomain);
  }
  setUseSecureCookies(useSecureCookies) {
    this.cookie.setUseSecureCookies(useSecureCookies);
  }
  addEnabledFeatureGates(enabledFeatureGates) {
    const gateArrayIsValid = Array.isArray(enabledFeatureGates) && enabledFeatureGates.every(gate => typeof gate === 'string') && enabledFeatureGates.length > 0;
    if (!gateArrayIsValid) {
      return;
    }
    this.enabledFeatureGates = enabledFeatureGates;
  }
  showBanner() {
    if (!this.privacySettings) {
      logError('Hubspot privacy banner does not have privacy settings set');
      return;
    }
    switch (this.privacySettings.mode) {
      case OPT_IN:
        if (!this.optInHandler) {
          return;
        }
        this.optInHandler.showBanner();
        return;
      case COOKIES_BY_CATEGORY:
        if (!this.cookiesByCategoryHandler) {
          return;
        }
        this.cookiesByCategoryHandler.showModal();
        return;
      default:
        return;
    }
  }
  hasEnabledFeatureGate(gateName) {
    return this.enabledFeatureGates && arrayContainsStr(this.enabledFeatureGates, gateName);
  }
  setApiBaseUrl(baseUrl) {
    // TODO: should we regex validate that the url is a hubspot one for security?
    // lets not set the api base url more than once
    if (this.apiBaseUrl) {
      return;
    }
    this.apiBaseUrl = baseUrl;
  }
  getBaseActivityPayload() {
    const bannerType = getBannerPolicyFromPrivacySetting(this.privacySettings);
    const bannerGeoLocation = this.visitorCountry;
    const bannerPolicyId = this.privacySettings.id;
    const portalId = this.privacySettings.portalId;
    const domainName = this.context.getHostName();

    // hs variables
    const contentId = this.context.win.hsVars && this.context.win.hsVars['page_id'];
    return {
      bannerGeoLocation: bannerGeoLocation || '',
      bannerPolicyId,
      bannerType,
      contentId: contentId || '',
      portalId,
      domainName
    };
  }
  sendReportingClickActivity() {
    if (!this.privacyConsent || !this.privacySettings) {
      return;
    }
    const consentAllowed = this.privacyConsent && this.privacyConsent.allowed;
    const consentAnalytics = this.privacyConsent && this.privacyConsent.categories.analytics;
    const consentAdvertisement = this.privacyConsent && this.privacyConsent.categories.advertisement;
    const consentFunctionality = this.privacyConsent && this.privacyConsent.categories.functionality;
    const clickActivity = {
      consentAllowed,
      consentAnalytics,
      consentAdvertisement,
      consentFunctionality
    };
    const baseActivity = this.getBaseActivityPayload();
    const baseActivityKeys = Object.keys(baseActivity);
    baseActivityKeys.forEach(baseActivityKey => {
      clickActivity[baseActivityKey] = baseActivity[baseActivityKey];
    });
    if (this.isTestingEnabled) {
      logDebug('The banner is in test mode, no reporting event will be sent.');
      logDebug('We would have sent the following click activity');
      logDebug(clickActivity);
      return;
    }
    sendClickActivity(this.apiBaseUrl, clickActivity);
  }
  sendReportingViewActivity() {
    if (!this.privacySettings) {
      return;
    }
    const viewActivity = this.getBaseActivityPayload();
    if (this.isTestingEnabled) {
      logDebug('The banner is in test mode, no reporting event will be sent.');
      logDebug('We would have sent the following view activity');
      logDebug(viewActivity);
      return;
    }
    sendViewActivity(this.apiBaseUrl, viewActivity);
  }
  setPortalId(portalId) {
    this.portalId = portalId;
  }
  setBusinessUnitId(businessUnitId) {
    this.businessUnitId = businessUnitId;
  }
  useGoogleConsentModeV2() {
    if (this.useGCMV2) {
      return;
    }
    this.useGCMV2 = true;
    if (this.privacyConsent) {
      this.sendConsentToGCMV2(this.privacyConsent.getSafeCopy());
    }
  }
  sendConsentToGCMV2(consent) {
    if (this.visitorCountry) {
      sendConsentUpdateViaGCMV2(consent, this.visitorCountry, this.privacySettings);
      return;
    }
    if (this.gcmGeoLocationFetchPromise == null) {
      this.gcmGeoLocationFetchPromise = getCurrentGeoLocation(this.apiBaseUrl).then(geoLocationString => {
        this.visitorCountry = geoLocationString;
        return geoLocationString;
      });
    }
    this.gcmGeoLocationFetchPromise.then(geoLocationString => {
      sendConsentUpdateViaGCMV2(consent, geoLocationString, this.privacySettings);
    }).catch(e => {
      console.error(e);
      console.error('HS banner failed to retrieve geolocation');
    });
  }
}